import React from 'react';
import { MediaActions } from '../../UI/buttons/media-actions';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { ResultType } from '../../../types/banner';

const resolutionDisplayNames: Record<string, string> = {
  '300x250': '300x250',
  '300x600': '300x600',
  '310x482': '310x482',
  '400x600': '400x600',
  '1080x1920': '1080x1920',
  '1080x1080': '1080x1080',
  '1080x1350': '1080x1350',
  '1920x1080': '1920x1080'
};

type Resolutions = (typeof resolutionDisplayNames)[keyof typeof resolutionDisplayNames];

export const MediaPreview: React.FC<{
  type: ResultType;
  src: string;
  format?: string;
}> = ({ type, src, format }) => {
  const { screens, spacing } = useResponsiveSizes();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isHovered, setIsHovered] = React.useState(false);

  const getMaxDimensions = () => {
    if (screens.xs) return { maxWidth: 280, maxHeight: 220 };
    if (screens.sm) return { maxWidth: 360, maxHeight: 280 };
    if (screens.md) return { maxWidth: 420, maxHeight: 320 };
    if (screens.lg) return { maxWidth: 480, maxHeight: 360 };
    return { maxWidth: 450, maxHeight: 340 };
  };

  const { maxWidth, maxHeight } = getMaxDimensions();

  const isResolution = format && Object.values(resolutionDisplayNames).includes(format as Resolutions);

  const isImage = type === 'image' || type === 'gif';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        marginBottom: spacing.md
      }}
    >
      {/* Label de resolución */}
      {isResolution && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontSize: 12,
            color: 'rgb(75, 85, 99)',
            letterSpacing: '0.5px',
            marginBottom: spacing.xs,
            textTransform: 'uppercase',
            fontWeight: 600
          }}
        >
          {format}
        </div>
      )}

      {/* Contenedor con hover */}
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: maxWidth,
          maxHeight: maxHeight,
          borderRadius: 12,
          overflow: 'hidden',
          cursor: 'pointer'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Loading spinner */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: '#f8fafc',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: '2px solid #e2e8f0',
                borderTop: '2px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          </div>
        )}

        {/* Imagen o Video */}
        {isImage ? (
          <img
            src={src}
            alt='Asset preview'
            style={{
              maxWidth: '100%',
              maxHeight: maxHeight,
              height: 'auto',
              borderRadius: 12,
              display: 'block',
              transition: 'filter 0.3s ease, box-shadow 0.3s ease',
              filter: isHovered ? 'brightness(1.1)' : 'brightness(1)',
              boxShadow: isHovered ? '0 12px 40px rgba(0, 0, 0, 0.25)' : '0 8px 25px rgba(0, 0, 0, 0.15)',
              opacity: isLoading ? 0 : 1
            }}
            onLoad={() => setIsLoading(false)}
          />
        ) : (
          <video
            src={src}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: maxHeight,
              width: '100%',
              height: 'auto',
              display: 'block',
              transition: 'filter 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease',
              filter: isHovered ? 'brightness(1.1)' : 'brightness(1)',
              boxShadow: isHovered ? '0 12px 40px rgba(0, 0, 0, 0.25)' : '0 8px 25px rgba(0, 0, 0, 0.15)',
              opacity: isLoading ? 0 : 1
            }}
            onLoadedMetadata={() => setIsLoading(false)}
          />
        )}

        {/* Overlay con botones - SOLO EN EL CENTRO */}
        {!isLoading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
              padding: spacing.md,
              borderRadius: 16,
              background: isHovered ? 'rgba(0, 0, 0, 0.7)' : 'transparent',
              backdropFilter: isHovered ? 'blur(8px)' : 'none',
              opacity: isHovered ? 1 : 0,
              transition: 'all 0.3s ease',
              pointerEvents: isHovered ? 'auto' : 'none',
              zIndex: 3
            }}
          >
            <MediaActions type={type} src={src} isHovered={isHovered} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
